import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useEquipmentScope } from '../equipmentScope'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import type { DatesSetArg, EventClickArg, EventContentArg } from '@fullcalendar/core'
import type { DateClickArg } from '@fullcalendar/interaction'
import { CalendarDays, CalendarRange, ChevronLeft, ChevronRight, List, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import './planner-calendar.css'

/**
 * A calendar-agnostic event: date + title + a single hex color. Keeping the
 * shape generic (and forwarding `meta`) lets any feature reuse this calendar
 * and grow it (click-through, drag, creation) without rework.
 */
export type PlannerCalendarEvent = {
  id: string
  /** ISO all-day date, e.g. "2026-10-01". Used when `span` is absent. */
  date: string
  /** Bold primary line, e.g. the equipment / type name. */
  title: string
  /** Muted secondary line, e.g. "Calibration · 09:00". */
  subtitle?: string
  /** Hex color driving the card's accent bar, tint and dot. */
  color: string
  /** Solid, high-emphasis pill (non-routine one-off events). */
  solid?: boolean
  /** Prefix the title with a ★ (non-routine marker). */
  star?: boolean
  /** Caller-defined payload, returned on click. */
  meta?: unknown
}

export type PlannerView = 'dayGridMonth' | 'dayGridWeek' | 'listMonth'

const VIEW_OPTIONS: { id: PlannerView; label: string; Icon: LucideIcon }[] = [
  { id: 'dayGridWeek', label: 'Week', Icon: CalendarRange },
  { id: 'dayGridMonth', label: 'Month', Icon: CalendarDays },
  { id: 'listMonth', label: 'List', Icon: List },
]

function EventPill({ event }: { event: PlannerCalendarEvent }) {
  const { color } = event
  return (
    <div
      title={event.subtitle ? `${event.title} — ${event.subtitle}` : event.title}
      className='eq-event-card flex w-full min-w-0 items-center gap-1.5 rounded-md border-l-[3px] py-1 pl-2 pr-1.5'
      style={{ backgroundColor: `${color}1f`, borderColor: color }}
    >
      <div className='min-w-0 flex-1 leading-tight'>
        <div className='flex items-center gap-1 truncate text-[11px] font-semibold text-gray-800'>
          {event.star && <span className='shrink-0' style={{ color }}>★</span>}
          <span className='truncate'>{event.title}</span>
        </div>
        {event.subtitle && (
          <div className='truncate text-[10px] font-medium' style={{ color }}>
            {event.subtitle}
          </div>
        )}
      </div>
      <span className='h-2 w-2 shrink-0 rounded-full' style={{ backgroundColor: color }} />
    </div>
  )
}

export function PlannerCalendar({
  events,
  initialView = 'dayGridMonth',
  focusDate,
  focusView,
  onRangeChange,
  onEventClick,
  onDateClick,
  toolbarExtras,
  footer,
}: {
  events: PlannerCalendarEvent[]
  initialView?: PlannerView
  /**
   * Jump the calendar to this ISO day. Declarative rather than an imperative
   * ref so an external picker can drive navigation without racing the toolbar's
   * own prev/next — FullCalendar stays the single owner of the visible range.
   */
  focusDate?: string | null
  /** Switch view alongside a jump (e.g. a "Week" preset). */
  focusView?: PlannerView | null
  /** Fires on mount and whenever the visible range changes ([start, end) ISO dates). */
  onRangeChange?: (start: string, end: string, title: string) => void
  onEventClick?: (event: PlannerCalendarEvent) => void
  /** Fires when an empty day cell is clicked (ISO date). */
  onDateClick?: (date: string) => void
  /** Right-hand toolbar slot (filters, actions…). */
  toolbarExtras?: ReactNode
  footer?: ReactNode
}) {
  const { copy } = useEquipmentScope()
  const calendarRef = useRef<FullCalendar>(null)
  const [title, setTitle] = useState('')
  const [view, setView] = useState<PlannerView>(initialView)
  const [todayInView, setTodayInView] = useState(true)

  const api = () => calendarRef.current?.getApi()

  // Apply the requested view BEFORE the date: changeView re-anchors the range,
  // so jumping first and switching after can land on a different period.
  useEffect(() => {
    if (focusView) calendarRef.current?.getApi()?.changeView(focusView)
  }, [focusView])

  useEffect(() => {
    if (focusDate) calendarRef.current?.getApi()?.gotoDate(focusDate)
  }, [focusDate])

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      setTitle(arg.view.title)
      setView(arg.view.type as PlannerView)
      const now = new Date()
      setTodayInView(arg.start <= now && now < arg.end)
      onRangeChange?.(arg.startStr.slice(0, 10), arg.endStr.slice(0, 10), arg.view.title)
    },
    [onRangeChange],
  )

  const renderEvent = useCallback((arg: EventContentArg) => {
    const pev = arg.event.extendedProps.pev as PlannerCalendarEvent
    return <EventPill event={pev} />
  }, [])

  const handleEventClick = useCallback(
    (arg: EventClickArg) => {
      onEventClick?.(arg.event.extendedProps.pev as PlannerCalendarEvent)
    },
    [onEventClick],
  )

  return (
    <div className='planner-cal'>
      {/* Toolbar */}
      <div className='mb-4 flex flex-wrap items-center gap-3'>
        <div className='flex items-center gap-1'>
          <Button variant='outline' size='icon' className='h-8 w-8' onClick={() => api()?.prev()} aria-label='Previous'>
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <Button variant='outline' size='icon' className='h-8 w-8' onClick={() => api()?.next()} aria-label='Next'>
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
        <h2 className='min-w-[160px] text-base font-semibold text-gray-900'>{title}</h2>

        <div className='ml-auto flex flex-wrap items-center gap-3'>
          <div className='flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5'>
            <button
              type='button'
              onClick={() => api()?.today()}
              disabled={todayInView}
              title={todayInView ? 'Already showing today' : 'Jump back to today'}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                todayInView
                  ? 'cursor-default text-gray-300'
                  : 'font-semibold text-primary hover:bg-white',
              )}
            >
              Today
            </button>
            <span className='mx-0.5 h-4 w-px bg-gray-200' />
            {VIEW_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type='button'
                onClick={() => api()?.changeView(opt.id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  view === opt.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-900',
                )}
              >
                <opt.Icon className='h-3.5 w-3.5' />
                {opt.label}
              </button>
            ))}
          </div>
          {toolbarExtras}
        </div>
      </div>

      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
        initialView={initialView}
        headerToolbar={false}
        height='auto'
        dayMaxEventRows={4}
        events={events.map((ev) => ({
          id: ev.id,
          title: ev.title,
          start: ev.date,
          allDay: true,
          extendedProps: { pev: ev },
        }))}
        eventContent={renderEvent}
        eventClick={handleEventClick}
        dateClick={(arg: DateClickArg) => onDateClick?.(arg.dateStr.slice(0, 10))}
        datesSet={handleDatesSet}
        listDayFormat={{ month: 'short', day: 'numeric' }}
        listDaySideFormat={{ weekday: 'short' }}
        noEventsContent={copy.noEvents}
      />

      {footer && <div className='mt-3'>{footer}</div>}
    </div>
  )
}
